import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import styled from "@emotion/styled";
import AddIcon from "@mui/icons-material/Add";
import {
  AppBar,
  Box,
  Container,
  Dialog,
  DialogContent,
  Grid,
  InputBase,
  Toolbar,
  Typography
} from "@mui/material";
import { cloneDeep } from "lodash";
import { useRef } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { Button } from "../../components/Button";
import SectionContent from "../../components/checklists/SectionContent";
import SectionItem, { CustomSectionPreview } from "../../components/checklists/SectionItem";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useChecklistDialog } from "../../hooks/useChecklistDialog";
import { useLocale } from "../../providers/LocaleProvider";
import Logo from "../../resources/images/logo.svg";
import { onOpenChecklistPreviewDialog } from "../../store/checklist.slice";

const SettingsChecklistsCreate = ({ isUpdateMode = false }: { isUpdateMode?: boolean }) => {
  const {
    form,
    control,
    sections,
    isChangedForm,
    isError,
    listQuestionTypes,
    selectedSectionIndex,
    openPreviewDialog,
    disableAddSection,
    resetSeq,
    checklists,
    setSelectedSectionIndex,
    handleAddSection,
    handleRemoveSection,
    handleFormSubmit,
    resetForm,
    handleSectionDragEnd,
    watch,
    activeId,
    setActiveId,
    triggerValidationValueDebounced,
    setOpenPreviewDialog,
    onClose,
    hasSectionError
  } = useChecklistDialog(isUpdateMode);

  const dispatch = useAppDispatch();
  const { translate } = useLocale();
  const dialogContentRef = useRef<HTMLDivElement>(null);

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  );

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const activeSection = sections?.find((section: any) => section.id === activeId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeIndex = sections?.findIndex((item: any) => item.id === active.id);
      const overIndex = sections?.findIndex((item: any) => item.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        handleSectionDragEnd(activeIndex, overIndex);
      }
    }
    setActiveId("");
    triggerValidationValueDebounced();
  };

  const scrollToTop = () => {
    dialogContentRef.current?.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <>
      <Dialog open={true} fullScreen={true} onClose={onClose}>
        <DialogContent
          ref={dialogContentRef}
          sx={{
            backgroundColor: "#FFF",
            marginBottom: 6,
            paddingBottom: 8
          }}
        >
          <FormProvider {...form}>
            <Container
              sx={{ height: "100%", paddingTop: 15 }}
              component={"form"}
              onSubmit={handleFormSubmit}
              id='checklists-create-form'
            >
              <Box height='100%' display={"flex"} flexDirection={"column"} gap={3}>
                <Grid container spacing={6}>
                  {/* Columns Sections */}
                  <Grid item xs={4}>
                    <Controller
                      control={control}
                      name='checklist.name'
                      render={({ field, fieldState }) => (
                        <InputBase
                          multiline
                          className='klayo-paper_title'
                          value={field.value}
                          fullWidth={false}
                          placeholder={translate(
                            "settingsViews.checklists.checklistNamePlaceholder"
                          )}
                          onChange={(e) => {
                            field.onChange(e);
                            triggerValidationValueDebounced?.();
                          }}
                          error={!!fieldState.error}
                          sx={{
                            marginBottom: 5,
                            borderRadius: "5px",
                            "& input": {
                              fontSize: "18px",
                              fontWeight: 600,
                              padding: "8px 12px !important",
                              border: fieldState.error
                                ? "1px solid var(--torch-red-tr-500)"
                                : "1px solid transparent",
                              borderRadius: "5px",
                              width: "100%"
                            },
                            width: "100%"
                          }}
                        />
                      )}
                    />

                    <DndContext
                      sensors={sensors}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={sections?.map((item: any) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Box>
                          {sections?.map((section, sectionIndex) => (
                            <Box key={section.id}>
                              <SectionItem
                                section={section}
                                sectionIndex={sectionIndex}
                                selectedSectionIndex={selectedSectionIndex}
                                onSelect={setSelectedSectionIndex}
                                onRemove={handleRemoveSection}
                                canRemove={sections?.length > 1}
                                hasError={hasSectionError(sectionIndex)}
                                scrollToTop={scrollToTop}
                              />
                            </Box>
                          ))}
                        </Box>
                      </SortableContext>
                      <DragOverlay zIndex={2000} dropAnimation={null}>
                        {activeSection ? (
                          <CustomSectionPreview section={activeSection as any} />
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                    {disableAddSection ? (
                      <Typography className='klayo-paper_title !important'>
                        {translate("settingsViews.checklists.maximumSectionsReached")}
                      </Typography>
                    ) : (
                      <Button
                        startIcon={<AddIcon />}
                        size='md'
                        variant='text'
                        label={translate("common.newSection")}
                        type='button'
                        onClick={handleAddSection}
                        disabled={disableAddSection}
                      />
                    )}
                  </Grid>

                  {/* Columns Questions */}
                  <Grid item xs={8}>
                    {sections?.length > 0 && (
                      <SectionContent
                        key={`${resetSeq}-${selectedSectionIndex}`}
                        sectionIndex={selectedSectionIndex}
                        listQuestionTypes={listQuestionTypes}
                        name={`checklist.sections.${selectedSectionIndex}`}
                        activeId={activeId}
                        setActiveId={setActiveId}
                        triggerValidationValueDebounced={triggerValidationValueDebounced}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Container>
          </FormProvider>
        </DialogContent>
        {/* Top App Bar */}
        <TopAppBar>
          <Toolbar>
            <Box
              className='klaro-appbar_logocontainer'
              sx={{ height: { xs: "24px!important", sm: "32px!important" } }}
            >
              <Logo className='klaro-appbar_logo' height='100%' />
            </Box>
            <Box
              sx={{
                gap: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                width: "100%"
              }}
            >
              <Button
                label={translate("common.cancel")}
                variant='outlined'
                size='md'
                onClick={() => {
                  onClose();
                  resetForm();
                }}
              />
              <Button
                size='md'
                variant='outlined'
                label={translate("common.preview")}
                onClick={() =>
                  dispatch(
                    onOpenChecklistPreviewDialog({
                      checklistData: cloneDeep(checklists)
                    })
                  )
                }
              />
              <Button
                size='md'
                variant='filled'
                label={isUpdateMode ? translate("common.save") : translate("common.create")}
                disabled={!isChangedForm || isError}
                type='submit'
                form='checklists-create-form'
              />
            </Box>
          </Toolbar>
        </TopAppBar>
      </Dialog>
    </>
  );
};

const TopAppBar = styled(AppBar)`
  top: 0;
  bottom: unset;
  background-color: #fff;
  box-shadow: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
`;

export default SettingsChecklistsCreate;
